import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import myShopClient from '@services/nearlive';
import { SESSION_STORAGE_KEYS, CONNECTION_KIND } from '../constants';

const OAUTH_POLLING_EVENT = 'oauth-callback-complete';

const URL_PARAM_SESSION_ID = 'connectSessionId';
const URL_PARAM_CONNECTION_KIND = 'connectionKind';

const stripCallbackParams = () => {
	const cleanUrl =
		window.location.pathname +
		window.location.search
			.replace(/[?&](code|state|error|error_description|connectSessionId|connectionKind)=[^&]*/g, '')
			.replace(/^&/, '?')
			.replace(/\?$/, '');
	window.history.replaceState({}, '', cleanUrl);
};

const normaliseKind = (kind) => (kind === CONNECTION_KIND.PRODUCT ? CONNECTION_KIND.PRODUCT : CONNECTION_KIND.STOCK);

/**
 * Handles OAuth callback from URL parameters.
 *
 * The connect-session ID is the primary thing we need to recover here. We try:
 *   1. sessionStorage (set by useConnectionState before the redirect)
 *   2. The connectSessionId/connectionKind query params carried back in OAuth state
 *
 * The URL fallback exists because sessionStorage is per-tab — if the OAuth round-trip
 * lands in a different tab/browser context (in-app browsers, popup-driven flows),
 * sessionStorage is empty on arrival.
 */
export const useOAuthCallback = () => {
	const [isProcessing, setIsProcessing] = useState(false);

	useEffect(() => {
		const handleOAuthCallback = async () => {
			const urlParams = new URLSearchParams(window.location.search);
			const code = urlParams.get('code');

			if (!code) {
				return;
			}

			const stockSessionId = sessionStorage.getItem(SESSION_STORAGE_KEYS.STOCK_SESSION);
			const productSessionId = sessionStorage.getItem(SESSION_STORAGE_KEYS.PRODUCT_SESSION);
			const urlSessionId = urlParams.get(URL_PARAM_SESSION_ID);
			const urlConnectionKind = urlParams.get(URL_PARAM_CONNECTION_KIND);

			// URL session id wins when present — it's the id the backend embedded into `state`
			// for this specific round-trip, so it can't be stale. sessionStorage is the fallback
			// for legacy initiations that predate the state-recovery fix.
			let sessionId = null;
			let connectionKind = null;

			if (urlSessionId) {
				sessionId = urlSessionId;
				if (urlSessionId === stockSessionId) {
					connectionKind = CONNECTION_KIND.STOCK;
				} else if (urlSessionId === productSessionId) {
					connectionKind = CONNECTION_KIND.PRODUCT;
				} else {
					connectionKind = normaliseKind(urlConnectionKind);
				}
			} else if (stockSessionId) {
				sessionId = stockSessionId;
				connectionKind = CONNECTION_KIND.STOCK;
			} else if (productSessionId) {
				sessionId = productSessionId;
				connectionKind = CONNECTION_KIND.PRODUCT;
			}

			if (!sessionId) {
				console.error('OAuth callback could not resolve a connect-session id from sessionStorage or URL', {
					hasCode: true,
					hasUrlSessionId: !!urlSessionId,
					hasStockSession: !!stockSessionId,
					hasProductSession: !!productSessionId
				});
				// Telemetry — NEA-6043 follow-up. The 14-day silence on `oauth-inventory-connection`
				// before that fix existed only because we had no signal the SPA hand-off was
				// failing. Sentry captureMessage surfaces this in the Issues feed so a regression
				// is alertable, not invisible.
				Sentry.captureMessage('Unify OAuth callback could not resolve a connect-session id', {
					level: 'warning',
					tags: { feature: 'unify-onboarding', oauthOutcome: 'no-session-id' },
					extra: {
						hasUrlSessionId: !!urlSessionId,
						hasStockSession: !!stockSessionId,
						hasProductSession: !!productSessionId
					}
				});
				stripCallbackParams();
				window.dispatchEvent(
					new CustomEvent(OAUTH_POLLING_EVENT, {
						detail: {
							connectionKind: normaliseKind(urlConnectionKind),
							success: false,
							error: "We couldn't finish connecting your POS. Please try again."
						}
					})
				);
				return;
			}

			setIsProcessing(true);

			// `source` records which side actually carried the session id we exchanged —
			// useful for monitoring how often sessionStorage survives the round-trip vs.
			// how often the URL fallback carries the load. Post-NEA-6043 the URL param
			// is present on every flow, so URL presence alone tells us nothing; the real
			// signal is whether sessionStorage *also* held the same id (round-trip
			// survived) — only then was the URL fallback unnecessary.
			const storageCarriedSession = urlSessionId ? urlSessionId === stockSessionId || urlSessionId === productSessionId : !!sessionId;
			const sessionSource = storageCarriedSession ? 'sessionStorage' : 'url';

			try {
				const client = await myShopClient();
				await client.post(`/api/onboarding/connect-sessions/${sessionId}/oauth`, {
					code,
					state: urlParams.get('state'),
					...Object.fromEntries(urlParams.entries())
				});

				stripCallbackParams();

				// Breadcrumb (not an Issue) on success — gives Sentry context for any later
				// error in the same session, without spamming the Issues feed for the happy path.
				Sentry.addBreadcrumb({
					category: 'unify-onboarding',
					message: 'Unify OAuth callback completed',
					level: 'info',
					data: { source: sessionSource, connectionKind }
				});

				window.dispatchEvent(
					new CustomEvent(OAUTH_POLLING_EVENT, {
						detail: { connectionKind, success: true }
					})
				);

				setIsProcessing(false);
			} catch (err) {
				stripCallbackParams();

				const errorMessage = err.response?.data?.error?.message || err.message || 'OAuth authorisation failed. Please try again.';

				// captureException on a real error object — Sentry keeps the stack and
				// groups by error fingerprint, which is what we want for repeated
				// provider-side failures.
				Sentry.captureException(err, {
					tags: { feature: 'unify-onboarding', oauthOutcome: 'exchange-error' },
					extra: { source: sessionSource, connectionKind, errorMessage }
				});

				window.dispatchEvent(
					new CustomEvent(OAUTH_POLLING_EVENT, {
						detail: {
							connectionKind,
							success: false,
							error: errorMessage
						}
					})
				);

				setIsProcessing(false);
			}
		};

		handleOAuthCallback();
	}, []);

	return {
		isProcessing
	};
};

export { OAUTH_POLLING_EVENT };
